import { useState } from 'react'
import { colors } from '../styles/tokens'

// =======================================================================
// ModuleExplanationPanel — the ONE generic renderer for a ModuleDefinition's
// `explanation` contract. This component contains NO module-specific
// text or logic — every word it displays comes from the module's own
// `explanation` object. Any current or future module (Rebar Slab, Rebar
// Beam, Column, Footing, Wall, Formwork, Concrete, ...) supplies its own
// explanation (and optional glossary); this renderer just presents
// whatever structure it is given, identically for all of them.
//
// The whole panel is collapsed by default (an estimator who already
// knows the module shouldn't have to see it every time), and the
// optional Glossary sub-section collapses independently within it.
// =======================================================================
export default function ModuleExplanationPanel({ explanation }) {
  const [open, setOpen] = useState(false)
  const [glossaryOpen, setGlossaryOpen] = useState(false)

  if (!explanation) return null

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 6, marginBottom: 14, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', textAlign: 'left', padding: '8px 12px', background: '#F0F4FF', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: colors.textPrimary, textTransform: 'uppercase', letterSpacing: 0.3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span>ℹ How this is calculated</span>
        <span style={{ fontSize: 10, color: colors.textMuted }}>{open ? '▾ Hide' : '▸ Show'}</span>
      </button>

      {open && (
        <div style={{ padding: '10px 12px', background: '#F8FAFF', fontSize: 11, color: colors.textSecondary }}>
          {explanation.method && (
            <div style={{ marginBottom: 8 }}>{explanation.method}</div>
          )}

          {explanation.assumptions?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, color: colors.textPrimary, marginBottom: 3 }}>Assumptions / Limitations</div>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {explanation.assumptions.map((a, i) => <li key={i} style={{ marginBottom: 2 }}>{a}</li>)}
              </ul>
            </div>
          )}

          {explanation.inputSources?.length > 0 && (
            <div style={{ marginBottom: explanation.glossary?.length > 0 ? 10 : 0 }}>
              <div style={{ fontWeight: 700, color: colors.textPrimary, marginBottom: 3 }}>Where each input comes from</div>
              {explanation.inputSources.map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
                  <span>{s.label}</span>
                  <span style={{ color: colors.textMuted, fontStyle: 'italic' }}>{s.source}</span>
                </div>
              ))}
            </div>
          )}

          {explanation.glossary?.length > 0 && (
            <div style={{ borderTop: `1px dashed ${colors.border}`, paddingTop: 8 }}>
              <button
                onClick={() => setGlossaryOpen((v) => !v)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: colors.primary, padding: 0 }}
              >
                {glossaryOpen ? '▾' : '▸'} Glossary — What do these terms mean?
              </button>
              {glossaryOpen && (
                <div style={{ marginTop: 6 }}>
                  {explanation.glossary.map((g, i) => (
                    <div key={i} style={{ marginBottom: 5 }}>
                      <strong style={{ color: colors.textPrimary }}>{g.term}</strong> — {g.definition}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
